<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <ui-chips v-model="orderStatus" :options="statusChips" :label="$t('order_status')" />
    </report-tools>

    <report-state v-if="loading && !report" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      :selected="isOpen"
      :row-class="workerRowClass"
      @sort="sortBy"
      @open="openWorker"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>

      <template #cell-shift="{ row, column }">
        <span v-if="row[column.key]" class="rs-tag rs-tag--count" :data-shift="column.shift.id">
          {{ row[column.key] }}
          <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: column.shift.id }) }}</span>
        </span>
        <template v-else>
          <span class="rd-dash" aria-hidden="true">–</span>
          <span class="rd-visually-hidden">0</span>
        </template>
      </template>

      <template #cell-status="{ row }">
        <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyReportTabEmployees from "@/components/company/reports/companyTabs/companyReportTabEmployees.vue";
import { SHIFTS } from "@/constants/shifts";
import hasWorkerList from "@/components/report/shared/hasWorkerList";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import ReportDesktopWorkerMonth from "@/components/report/desktop/screens/ReportDesktopWorkerMonth.vue";

// Šta je hitnije u sortiranju po statusu: nije preuzeo → deo → nije poručio → preuzeo.
const STATE_RANK = { none: 3, part: 2, off: 1, all: 0 };

/**
 * Radnici firme na računaru: spisak radnika izabranih firmi sa smenama u kojima su poručili, poručeno / preuzeto /
 * razlikom i statusom (Nije poručio, Nije preuzeo, deo, Preuzeo). Filter statusa su čipovi sa brojevima; klik na
 * red otvara mesečni pregled radnika u panelu. Firme i pretragu čita iz zajedničke trake. Nasleđuje učitavanje,
 * filter statusa, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopEmployees",
  extends: CompanyReportTabEmployees,
  mixins: [hasWorkerList, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    selectedId: null,
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Firma radnika za mesečni pregled: samo kad je izabrana jedna (inače se ne zna kojoj radnik pripada). */
    selectedCompany() {
      const companies = this.$store.state.report.selectedFoodCompanies || [];
      return companies.length === 1 ? companies[0] : null;
    },
    /** Čipovi statusa sa brojevima; vrednosti su isti statusi kao u starom izborniku (orderStatus). */
    statusChips() {
      const users = (this.report && this.report.users) || [];
      const ordered = users.filter((user) => (user.order_count || 0) > 0);
      const taken = ordered.filter((user) => (user.order_payed || 0) > 0).length;
      const counts = [users.length, ordered.length, users.length - ordered.length, taken, ordered.length - taken];
      return this.statusItems.map((item, index) => ({ value: item.value, label: item.text, count: counts[index] }));
    },
    /** Smene 1–3 uvek, četvrta samo kad neko u njoj ima narudžbu. */
    visibleShifts() {
      const users = this.filteredUsers;
      return SHIFTS.filter((shift) => shift.id < 4 || users.some((user) => Number(user[shift.key]) > 0));
    },
    hasExternalCode() {
      return this.filteredUsers.some((user) => user.external_code);
    },
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "id", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("worker"), name: true, sortable: true },
        ...(this.hasBranches
          ? [{ key: "companyName", label: this.$t("branch"), muted: true, sortable: true, value: (row) => row.companyName || "–", title: (row) => row.companyName }]
          : []),
        ...(this.hasExternalCode
          ? [{ key: "externalCode", label: this.$t("external_code"), num: true, muted: true, sortable: true, value: (row) => row.externalCode || "–" }]
          : []),
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "center", sortable: true })),
        { key: "ordered", label: this.$t("ordered"), align: "end", num: true, zero: true, sortable: true },
        { key: "taken", label: this.$t("taken"), cls: "rd-col-taken", align: "end", num: true, zero: true, sortable: true },
        {
          key: "difference",
          label: this.$t("difference"),
          align: "end",
          num: true,
          zero: true,
          sortable: true,
          cellClass: (row) => ({ "is-alert": row.difference > 0 }),
        },
        { key: "status", label: this.$t("status"), sortKey: "stateRank", sortable: true },
        { key: "go", label: "", go: true },
      ];
    },
    allRows() {
      return this.filteredUsers.map((user) => this.toRow(user));
    },
    rows() {
      const search = this.filters.search;
      const matched = search
        ? this.customSearch(this.allRows, [{ value: "name" }, { value: "idText" }, { value: "externalCode" }, { value: "companyName" }], search)
        : this.allRows;
      // abecedno kao druga mera: redovi sa istom vrednošću ostaju po imenu
      const byName = [...matched].sort((a, b) => a.name.localeCompare(b.name));
      return this.sortRows(byName);
    },
    totals() {
      const totals = { ordered: 0, taken: 0, difference: 0 };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = 0;
      });
      this.rows.forEach((row) => {
        totals.ordered += row.ordered;
        totals.taken += row.taken;
        totals.difference += row.difference;
        SHIFTS.forEach((shift) => {
          totals[shift.key] += row[shift.key];
        });
      });
      return totals;
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.totals.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.totals.taken },
        { key: "difference", label: this.$t("difference"), value: this.totals.difference },
      ];
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.report && this.report.users && this.report.users.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    /** Red tabele: radnik iz odgovora (users) + stanje narudžbe i preuzimanja. */
    toRow(user) {
      const ordered = user.order_count || 0;
      const taken = user.order_payed || 0;
      const state = ordered <= 0 ? "off" : taken <= 0 ? "none" : taken >= ordered ? "all" : "part";
      const name = [this.toLatin(user.name), this.toLatin(user.lastname)].filter(Boolean).join(" ").trim();
      const row = {
        id: user.id,
        idText: String(user.id),
        externalCode: user.external_code || "",
        companyName: this.toLatin(user.company_name || ""),
        name,
        initials: [user.name, user.lastname]
          .filter(Boolean)
          .map((part) => this.toLatin(part).charAt(0))
          .join(""),
        ordered,
        taken,
        difference: ordered - taken,
        state,
        stateRank: STATE_RANK[state],
        statusLabel: state === "off" ? this.$t("report_desktop.status_not_ordered") : this.statusLabel(state, taken, ordered),
        source: user,
      };
      SHIFTS.forEach((shift) => {
        row[shift.key] = Number(user[shift.key]) || 0;
      });
      return row;
    },
    workerRowClass(row) {
      return { "is-idle": row.state === "off" };
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu. */
    openWorker(worker, event) {
      this.selectedId = worker.id;
      const employee = this.toEmployeeContext(worker);
      this.$emit("open-panel", {
        opener: event && event.currentTarget,
        key: `worker-${worker.id}`,
        root: true,
        wide: true,
        title: worker.name,
        subtitle: [`ID ${worker.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { worker: employee, compact: true },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasBranches ? [this.$t("branch")] : []),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasBranches ? [row.companyName] : []),
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
